/* eslint-disable react/react-in-jsx-scope */
import IconExcel from "@/assets/icones/excel.svg?react";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { toast } from "@/components/ui/use-toast";
import {
  DASHBOARD_QUERY_GC_TIME,
  DASHBOARD_QUERY_STALE_TIMES,
  dashboardQueryKeys,
} from "@/dashboard/dashboardQueryConfig";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import i18n from "@/i18n";
import { convertToEuro } from "@/libs/amount";
import {
  buildUserAccessBatchEndpoint,
  buildUserAccessBatchEndpoints,
  mergeBatchAccessPayloads,
  normalizeUserAccessMemberIds,
  readBatchAccessResponses,
} from "@/libs/subcontractorAccess";
import {
  cn,
  formatIndicatorImpactUnitLabel,
  formatIndicatorUnitLabel,
  getChartDataYear,
} from "@/libs/utils";
import {
  getSynthesesMetricToneClasses,
  type SynthesesMetricTone,
} from "@/pages/dashboard/syntheses/synthesesMetricTones";
import useSynthesesGroupedContractsRead from "@/pages/dashboard/syntheses/useSynthesesGroupedContractsRead";
import { ProgramType } from "@/types/dashboard";
import {
  AlertTriangle,
  BriefcaseBusiness,
  CalendarDays,
  ChevronDown,
  Download,
  FileText,
  FileSpreadsheet,
  Loader,
} from "lucide-react";
import { type ReactNode, lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import ListingProject from "./ListingProject";

const LazyProgramCard = lazy(() => import("../program/ProgramCarousel"));
const LazyChartsDashboard = lazy(() => import("./ChartsDashboard"));
const LazyStatsColoredDashboard = lazy(() => import("./StatsDashboard"));

type PendingVerdeoAllocationWarning = {
  projectId: number;
  projectName: string;
  pendingContracts: number;
  pendingAmountEur: number;
};

type DashboardYearMatrixRow = {
  id: string;
  label: string;
  valuesByYear: Record<string, number>;
  total: number;
};

type DashboardYearMatrixData = {
  years: string[];
  rows: DashboardYearMatrixRow[];
  columnTotals: Record<string, number>;
  grandTotal: number;
};

type ProjectTypeFundingInfoResponse = {
  totalFunding: [number, number];
  fundingByType: Array<{
    type: {
      id: number;
      name: string;
      projectTypeLangs?: Array<{
        name?: string;
        lang?: {
          code?: string;
        };
      }>;
    };
    total: number;
    obtained: number;
  }>;
};

type DashboardSubcontractorItem = {
  id: number;
  name: string;
  email: string;
  memberIds: number[];
  memberCount: number;
};

type DashboardAccessProgramResponse = Array<{
  id?: number;
  name?: string;
  access?: boolean;
  projects?: Array<{
    id?: number;
    name?: string;
    access?: boolean;
  }>;
}>;

type GenericRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is GenericRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normalizeCollectionLike = (value: unknown): GenericRecord[] => {
  if (Array.isArray(value)) return value.filter(isRecord);
  if (!isRecord(value)) return [];

  if (Array.isArray(value["hydra:member"])) {
    return value["hydra:member"].filter(isRecord);
  }
  if (Array.isArray(value.member)) {
    return value.member.filter(isRecord);
  }
  if (Array.isArray(value.items)) {
    return value.items.filter(isRecord);
  }

  const values = Object.values(value).filter(isRecord);
  if (values.length === 0) return [];

  return values;
};

const normalizeDashboardPrograms = (value: unknown): any[] =>
  normalizeCollectionLike(value).map((program) => ({
    ...program,
    projects: normalizeCollectionLike(program.projects),
  }));

const readFiniteNumber = (value: unknown): number => {
  const num = Number(value ?? 0);
  return Number.isFinite(num) ? num : 0;
};

const readFundingAmountEuro = (item: any): number => {
  const convertedAmount = Number(item?.convertedAmount);
  if (Number.isFinite(convertedAmount) && convertedAmount > 0) {
    return convertedAmount;
  }

  const amount = Number(item?.amount);
  const currencyCode = toTrimmedText(item?.devise?.code).toUpperCase();
  if (Number.isFinite(amount) && amount > 0 && (currencyCode === "" || currencyCode === "EUR")) {
    return amount;
  }

  return 0;
};

const toTrimmedText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const formatEuro = (value: number) =>
  `${Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

const sanitizeAsciiFilename = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7E]/g, "")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

const getFileNameFromDisposition = (
  disposition: string | null,
  fallback: string,
) => {
  const asciiFallback = sanitizeAsciiFilename(fallback);
  if (!disposition) return asciiFallback;

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const simpleMatch = disposition.match(/filename=\"?([^\"]+)\"?/i);
  return simpleMatch?.[1] ?? asciiFallback;
};

const downloadDashboardExcelExport = async (payload: Record<string, unknown>) => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");
  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/dashboard/export/xlsx`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    throw new Error("Erreur lors de l'export du dashboard");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getFileNameFromDisposition(
    response.headers.get("content-disposition"),
    "Rapport-dashboard.xlsx",
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const downloadDashboardWordExport = async (payload: Record<string, unknown>) => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");
  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/dashboard/export/docx`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    throw new Error("Erreur lors de l'export Word du dashboard");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getFileNameFromDisposition(
    response.headers.get("content-disposition"),
    "Rapport-dashboard.docx",
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getYearFromIsoDate = (value: unknown) => {
  const normalizedValue = toTrimmedText(value);
  const matchedYear = normalizedValue.match(/^(\d{4})/);
  if (matchedYear?.[1]) return matchedYear[1];

  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return "";
  return String(date.getFullYear());
};

const getDaysElapsedDashboard = (value: unknown) => {
  const date = new Date(String(value ?? ""));
  if (Number.isNaN(date.getTime())) return 0;
  return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
};

const buildDashboardActiveProjectSections = ({
  projects,
  currentUserId,
  locale,
  labels,
}: {
  projects: any[];
  currentUserId: number;
  locale: string;
  labels: {
    awaitingYourValidation: string;
    awaitingVerdeoValidation: string;
    inProgress: string;
    me: string;
    creators: string;
    subcontractors: string;
    approvalRequest: string;
    creationDate: string;
    waitingDuration: string;
    sinceWhen: string;
  };
}) => {
  const sections = {
    WaitingValidationPP: {
      title: labels.awaitingYourValidation,
      secondColumnLabel: labels.subcontractors,
      dateColumnLabel: labels.approvalRequest,
      durationColumnLabel: labels.waitingDuration,
      less2: 0,
      more2: 0,
      more4: 0,
      rows: [] as Array<Record<string, string>>,
    },
    WaitingValidation: {
      title: labels.awaitingVerdeoValidation,
      secondColumnLabel: labels.creators,
      dateColumnLabel: labels.approvalRequest,
      durationColumnLabel: labels.waitingDuration,
      less2: 0,
      more2: 0,
      more4: 0,
      rows: [] as Array<Record<string, string>>,
    },
    InProgress: {
      title: labels.inProgress,
      secondColumnLabel: labels.creators,
      dateColumnLabel: labels.creationDate,
      durationColumnLabel: labels.sinceWhen,
      less2: 0,
      more2: 0,
      more4: 0,
      rows: [] as Array<Record<string, string>>,
    },
  };

  (Array.isArray(projects) ? projects : []).forEach((project: any) => {
    const status = Number(project?.status || 0);
    if (![1, 2, 6].includes(status)) return;

    const sourceDate = status === 1 ? project?.approvalRequest : project?.createdAt;
    const jours = getDaysElapsedDashboard(sourceDate);
    const sectionKey =
      status === 1
        ? "InProgress"
        : status === 2
          ? "WaitingValidation"
          : "WaitingValidationPP";
    const section = sections[sectionKey];

    if (jours > 27) {
      section.more4 += 1;
    } else if (jours > 13) {
      section.more2 += 1;
    } else {
      section.less2 += 1;
    }

    section.rows.push({
      projectName: String(project?.name ?? "-"),
      ownerLabel:
        project?.creator == null || Number(project?.creator?.id || 0) === currentUserId
          ? labels.me
          : project?.program?.appelAProjets == null
            ? String(project?.creator?.email ?? "-")
            : String(project?.program?.appelAProjets?.email ?? "-"),
      dateLabel: new Date(String(sourceDate ?? "")).toLocaleDateString(locale || "fr", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      durationLabel: `${jours} jour(s)`,
    });
  });

  return [
    sections.WaitingValidationPP,
    sections.WaitingValidation,
    sections.InProgress,
  ];
};

const getCurrentUserStructureName = () => {
  if (typeof window === "undefined") return "";

  try {
    const rawUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");
    if (!rawUser) return "";

    const parsedUser = JSON.parse(rawUser);
    return toTrimmedText(
      parsedUser?.structureName ||
        parsedUser?.Organisation?.name ||
        parsedUser?.fullname ||
        "",
    );
  } catch {
    return "";
  }
};

const useDeferredActivation = (delayMs: number) => {
  const [active, setActive] = useState(() => typeof window === "undefined");

  useEffect(() => {
    if (typeof window === "undefined") return;

    let timerId: number | null = null;
    const frameId = window.requestAnimationFrame(() => {
      timerId = window.setTimeout(() => {
        setActive(true);
      }, delayMs);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, [delayMs]);

  return active;
};

const useViewportActivation = (rootMargin = "600px") => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(() => typeof window === "undefined");

  useEffect(() => {
    if (typeof window === "undefined" || active) return;

    const targetNode = targetRef.current;
    if (!targetNode || !("IntersectionObserver" in window)) {
      setActive(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        setActive(true);
        observer.disconnect();
      },
      {
        rootMargin,
      },
    );

    observer.observe(targetNode);

    return () => {
      observer.disconnect();
    };
  }, [active, rootMargin]);

  return [targetRef, active] as const;
};

const DashboardSecondaryInsightsSkeleton = () => (
  <>
    <div className="mx-5 mb-5 rounded-xl bg-card p-5">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={index}
            className="h-[112px] rounded-xl bg-[#ecedee]"
          />
        ))}
      </div>
    </div>
    <div className="mx-5 mb-5 space-y-5">
      <Skeleton className="h-[280px] rounded-xl bg-[#ecedee]" />
      <Skeleton className="h-[280px] rounded-xl bg-[#ecedee]" />
    </div>
  </>
);

const DashboardLargeSectionSkeleton = ({
  heightClass = "h-[280px]",
}: {
  heightClass?: string;
}) => (
  <div className="rounded-xl bg-card p-5">
    <Skeleton className={`${heightClass} rounded-xl bg-[#ecedee]`} />
  </div>
);

const getCurrentUserDashboardId = () => {
  if (typeof window === "undefined") return 0;

  try {
    const rawUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");
    if (!rawUser) return 0;

    const parsedUser = JSON.parse(rawUser);
    return Number(parsedUser?.id || 0);
  } catch {
    return 0;
  }
};

const extractProjectsFromRealFundingsResponse = (raw: unknown): GenericRecord[] => {
  const direct = normalizeCollectionLike(raw);
  if (direct.length > 0) return direct;

  if (isRecord(raw)) {
    const fromData = normalizeCollectionLike(raw.data);
    if (fromData.length > 0) return fromData;

    const fromProjects = normalizeCollectionLike(raw.projects);
    if (fromProjects.length > 0) return fromProjects;
  }

  return [];
};

const collectPendingVerdeoAllocationWarnings = (
  raw: unknown,
): PendingVerdeoAllocationWarning[] => {
  const projects = extractProjectsFromRealFundingsResponse(raw);
  if (projects.length === 0) return [];

  return projects
    .map((project) => {
      const projectId = Number(project?.id);
      if (!projectId || Number.isNaN(projectId)) return null;

      const hasPending = Boolean(
        project?.hasPendingVerdeoAllocation ??
          project?.has_pending_verdeo_allocation,
      );
      const pendingAmountEur = readFiniteNumber(
        project?.pendingVerdeoAllocationAmount ??
          project?.pending_verdeo_allocation_amount,
      );
      const pendingContracts = Math.max(
        1,
        Math.round(
          readFiniteNumber(
            project?.pendingVerdeoAllocationContracts ??
              project?.pending_verdeo_allocation_contracts ??
            project?.pendingVerdeoAllocationPayments ??
              project?.pending_verdeo_allocation_payments,
          ),
        ),
      );

      if (!hasPending && pendingAmountEur <= 0) return null;

      return {
        projectId,
        projectName:
          String(project?.name ?? project?.title ?? "").trim() ||
          `Projet #${projectId}`,
        pendingContracts,
        pendingAmountEur,
      } satisfies PendingVerdeoAllocationWarning;
    })
    .filter((warning): warning is PendingVerdeoAllocationWarning => warning !== null)
    .map((warning) => ({
      ...warning,
      pendingAmountEur: Math.round(
        (warning.pendingAmountEur + Number.EPSILON) * 100,
      ) / 100,
    }))
    .sort((a, b) => b.pendingContracts - a.pendingContracts);
};

const addAmountByYear = (
  valuesByYear: Record<string, number>,
  year: string,
  amountEuro: number,
) => {
  if (!/^\d{4}$/.test(year)) return;
  valuesByYear[year] = Number(
    ((valuesByYear[year] || 0) + Number(amountEuro || 0)).toFixed(2),
  );
};

const contractContainsProject = (contract: any, projectId: number) => {
  if (!(Number(projectId) > 0)) return false;

  if (Number(contract?.project?.id || 0) === Number(projectId)) {
    return true;
  }

  const projectTotals = Array.isArray(contract?.projectTotals)
    ? contract.projectTotals
    : [];
  if (
    projectTotals.some(
      (item: any) => Number(item?.projectId || 0) === Number(projectId),
    )
  ) {
    return true;
  }

  const lineRows = Array.isArray(contract?.lineRows) ? contract.lineRows : [];
  return lineRows.some(
    (item: any) => Number(item?.projectId || 0) === Number(projectId),
  );
};

const normalizeDesiredFundingSourceIds = (lineRow: any) =>
  (Array.isArray(lineRow?.desiredFundingSourceIds)
    ? lineRow.desiredFundingSourceIds
    : []
  )
    .map((value: unknown) => Number(value || 0))
    .filter((value: number) => Number.isFinite(value) && value > 0);

const readSynthesesContractTargetId = (contract: any) => {
  const directId = Number(contract?.synthesesContractId || 0);
  if (Number.isFinite(directId) && directId > 0) {
    return directId;
  }

  const matched = String(contract?.id || "").match(/^synth-contract-(\d+)$/);
  if (!matched?.[1]) return null;

  const parsed = Number(matched[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const findPendingVerdeoContractTarget = (
  financeurs: any[],
  projectId: number,
) => {
  const candidates = (Array.isArray(financeurs) ? financeurs : []).flatMap(
    (financeur, financeurIndex) => {
      const contracts = Array.isArray(financeur?.contracts) ? financeur.contracts : [];

      return contracts
        .map((contract: any, contractIndex: number) => {
          if (!contractContainsProject(contract, projectId)) {
            return null;
          }

          const matchingLineRows = (Array.isArray(contract?.lineRows)
            ? contract.lineRows
            : []
          ).filter(
            (lineRow: any) => Number(lineRow?.projectId || 0) === Number(projectId),
          );
          const firstPendingLineRow =
            matchingLineRows.find(
              (lineRow: any) =>
                normalizeDesiredFundingSourceIds(lineRow).length === 0,
            ) || null;
          const fallbackLineRow = matchingLineRows[0] || null;
          const targetLineRow = firstPendingLineRow || fallbackLineRow;
          const isVerdeoCandidate =
            financeur?.isVerdeo === true || contract?.isVerdeo === true;

          return {
            financeur,
            contract,
            targetLineId: Number(targetLineRow?.lineId || 0) || null,
            targetProjectId: Number(targetLineRow?.projectId || 0) || projectId,
            score:
              (firstPendingLineRow ? 100 : 0) +
              (matchingLineRows.length > 0 ? 10 : 0) +
              (isVerdeoCandidate ? 5 : 0),
            order: financeurIndex * 1000 + contractIndex,
          };
        })
        .filter((candidate) => candidate !== null);
    },
  );

  if (candidates.length === 0) return null;

  candidates.sort((left, right) => {
    if (right.score !== left.score) {
      return right.score - left.score;
    }

    return left.order - right.order;
  });

  return candidates[0];
};

const buildPendingVerdeoSynthesesHref = ({
  projectId,
  projectName,
  financeurs,
}: {
  projectId: number;
  projectName: string;
  financeurs: any[];
}) => {
  const params = new URLSearchParams();
  const projectFilterKey =
    Number.isFinite(Number(projectId)) && Number(projectId) > 0
      ? `id:${Number(projectId)}`
      : "";

  if (projectFilterKey) {
    params.set("targetProjectFilterKey", projectFilterKey);
  }
  if (toTrimmedText(projectName)) {
    params.set("targetProjectName", toTrimmedText(projectName));
  }

  const preferredTarget = findPendingVerdeoContractTarget(financeurs, projectId);
  const preferredFinanceur =
    preferredTarget?.financeur ||
    (Array.isArray(financeurs) ? financeurs : []).find(
      (financeur) =>
        Array.isArray(financeur?.contracts) &&
        financeur.contracts.some((contract: any) =>
          contractContainsProject(contract, projectId),
        ),
    ) ||
    null;

  if (
    Number.isFinite(Number(preferredFinanceur?.id || 0)) &&
    Number(preferredFinanceur?.id || 0) > 0
  ) {
    params.set("targetFinanceurId", String(Number(preferredFinanceur?.id || 0)));
  }
  if (toTrimmedText(preferredFinanceur?.name)) {
    params.set("targetFinanceurName", toTrimmedText(preferredFinanceur?.name));
  }

  if (preferredTarget?.contract) {
    const contractNodeId = toTrimmedText(preferredTarget.contract?.id);
    const synthesesContractId = readSynthesesContractTargetId(
      preferredTarget.contract,
    );

    if (contractNodeId) {
      params.set("targetContractNodeId", contractNodeId);
    }
    if (
      Number.isFinite(Number(synthesesContractId || 0)) &&
      Number(synthesesContractId || 0) > 0
    ) {
      params.set("targetSynthesesContractId", String(Number(synthesesContractId)));
    }
    if (
      Number.isFinite(Number(preferredTarget.targetLineId || 0)) &&
      Number(preferredTarget.targetLineId || 0) > 0
    ) {
      params.set("targetLineId", String(Number(preferredTarget.targetLineId)));
    }
    if (
      Number.isFinite(Number(preferredTarget.targetProjectId || 0)) &&
      Number(preferredTarget.targetProjectId || 0) > 0
    ) {
      params.set(
        "targetProjectId",
        String(Number(preferredTarget.targetProjectId)),
      );
    }
    params.set("targetAction", "desired-funding-allocation");
  }

  const search = params.toString();
  return search
    ? `/dashboard/syntheses/financeurs?${search}`
    : "/dashboard/syntheses/financeurs";
};

const finalizeDashboardYearMatrix = (
  rows: DashboardYearMatrixRow[],
): DashboardYearMatrixData => {
  const yearSet = new Set<string>();

  rows.forEach((row) => {
    Object.entries(row.valuesByYear).forEach(([year, amountEuro]) => {
      if (/^\d{4}$/.test(year) && Number(amountEuro || 0) !== 0) {
        yearSet.add(year);
      }
    });
  });

  const years = Array.from(yearSet.values()).sort((a, b) => Number(a) - Number(b));
  const columnTotals = years.reduce<Record<string, number>>((acc, year) => {
    acc[year] = Number(
      rows.reduce((sum, row) => sum + Number(row.valuesByYear[year] || 0), 0).toFixed(2),
    );
    return acc;
  }, {});
  const grandTotal = Number(
    rows.reduce((sum, row) => sum + Number(row.total || 0), 0).toFixed(2),
  );

  return {
    years,
    rows,
    columnTotals,
    grandTotal,
  };
};

const buildFinanceurYearMatrix = (items: any[]): DashboardYearMatrixData => {
  const rows = (Array.isArray(items) ? items : [])
    .map((item) => {
      const valuesByYear: Record<string, number> = {};
      const contracts = Array.isArray(item?.contracts) ? item.contracts : [];

      contracts.forEach((contract: any) => {
        const lineRows = Array.isArray(contract?.lineRows) ? contract.lineRows : [];
        if (lineRows.length > 0) {
          lineRows.forEach((lineRow: any) => {
            const year = String(Number(lineRow?.year || 0) || "").trim();
            addAmountByYear(valuesByYear, year, Number(lineRow?.amountEuro || 0));
          });
          return;
        }

        const contractYears = Array.isArray(contract?.years) ? contract.years : [];
        const fallbackYear =
          String(Number(contractYears[0] || 0) || "").trim() ||
          getYearFromIsoDate(contract?.contractDate);
        addAmountByYear(valuesByYear, fallbackYear, Number(contract?.amountEuro || 0));
      });

      return {
        id: String(item?.id || item?.name || ""),
        label: toTrimmedText(item?.name) || "Financeur",
        valuesByYear,
        total: Number(
          Object.values(valuesByYear).reduce((sum, amount) => sum + Number(amount || 0), 0).toFixed(2),
        ),
      } satisfies DashboardYearMatrixRow;
    })
    .filter((row) => row.label.length > 0)
    .sort((a, b) => a.label.localeCompare(b.label, "fr"));

  return finalizeDashboardYearMatrix(rows);
};

const getDashboardSubcontractorName = (item: any) => {
  return [
    toTrimmedText(item?.lastname),
    toTrimmedText(item?.firstname),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
};

const getDashboardSubcontractorOrganisationName = (item: any) => {
  return toTrimmedText(item?.Organisation?.name || item?.organisation?.name || "");
};

const buildDashboardSubcontractors = (response: any): DashboardSubcontractorItem[] => {
  const rawItems = Array.isArray(response?.subcontractors)
    ? response.subcontractors
    : [];
  const grouped = new Map<string, DashboardSubcontractorItem>();

  rawItems.forEach((item: any) => {
    const userId = Number(item?.id || 0);
    if (!Number.isFinite(userId) || userId <= 0) return;

    const organisationId = Number(item?.Organisation?.id || item?.organisation?.id || 0);
    const organisationName = getDashboardSubcontractorOrganisationName(item);
    const normalizedOrganisationName = organisationName.toLocaleLowerCase("fr").trim();
    const groupKey = normalizedOrganisationName
      ? `organisation:${normalizedOrganisationName}`
      : `user:${userId}`;
    const groupId = Number.isFinite(organisationId) && organisationId > 0
      ? organisationId
      : -userId;
    const displayName =
      organisationName ||
      getDashboardSubcontractorName(item) ||
      `Intervenant #${userId}`;
    const email = toTrimmedText(item?.email);
    const current = grouped.get(groupKey);

    if (!current) {
      grouped.set(groupKey, {
        id: groupId,
        name: displayName,
        email,
        memberIds: [userId],
        memberCount: 1,
      });
      return;
    }

    if (!current.memberIds.includes(userId)) {
      current.memberIds.push(userId);
      current.memberCount += 1;
    }
    if (!current.email && email) {
      current.email = email;
    }
  });

  return Array.from(grouped.values()).sort((a, b) =>
    a.name.localeCompare(b.name, "fr"),
  );
};

const mergeDashboardAccessProgramResponses = (
  responses: Array<DashboardAccessProgramResponse | null | undefined>,
): DashboardAccessProgramResponse => {
  const programsMap = new Map<number, {
    id: number;
    name: string;
    access: boolean;
    projectsMap: Map<number, { id: number; name: string; access: boolean }>;
  }>();

  responses.forEach((response) => {
    const programs = Array.isArray(response) ? response : [];

    programs.forEach((program) => {
      const programId = Number(program?.id || 0);
      if (!Number.isFinite(programId) || programId <= 0) return;

      const existingProgram = programsMap.get(programId) ?? {
        id: programId,
        name: toTrimmedText(program?.name) || `Programme #${programId}`,
        access: false,
        projectsMap: new Map<number, { id: number; name: string; access: boolean }>(),
      };

      existingProgram.access = existingProgram.access || program?.access === true;

      const projects = Array.isArray(program?.projects) ? program.projects : [];
      projects.forEach((project) => {
        const projectId = Number(project?.id || 0);
        if (!Number.isFinite(projectId) || projectId <= 0) return;

        const existingProject = existingProgram.projectsMap.get(projectId) ?? {
          id: projectId,
          name: toTrimmedText(project?.name) || `Projet #${projectId}`,
          access: false,
        };

        existingProject.access = existingProject.access || project?.access === true;
        existingProgram.projectsMap.set(projectId, existingProject);
      });

      programsMap.set(programId, existingProgram);
    });
  });

  return Array.from(programsMap.values()).map((program) => ({
    id: program.id,
    name: program.name,
    access: program.access,
    projects: Array.from(program.projectsMap.values()),
  }));
};

const buildDashboardAccessibleProjectIds = (
  response: DashboardAccessProgramResponse | null | undefined,
) => {
  const projectIds = new Set<number>();
  const programs = Array.isArray(response) ? response : [];

  programs.forEach((program) => {
    const projects = Array.isArray(program?.projects) ? program.projects : [];
    projects.forEach((project) => {
      const projectId = Number(project?.id || 0);
      if (project?.access === true && Number.isFinite(projectId) && projectId > 0) {
        projectIds.add(projectId);
      }
    });
  });

  return projectIds;
};

const buildIntervenantYearMatrix = (
  groupedFinanceurs: any[],
  subcontractors: DashboardSubcontractorItem[],
  accessBySubcontractorId: Record<string, DashboardAccessProgramResponse>,
): DashboardYearMatrixData => {
  const rows = subcontractors
    .map((subcontractor) => {
      const projectIds = buildDashboardAccessibleProjectIds(
        accessBySubcontractorId[String(subcontractor.id)],
      );
      const valuesByYear: Record<string, number> = {};

      groupedFinanceurs.forEach((financeur) => {
        const contracts = Array.isArray(financeur?.contracts) ? financeur.contracts : [];

        contracts.forEach((contract: any) => {
          const contractLandPayments = Array.isArray(contract?.landPayments)
            ? contract.landPayments
            : [];
          const lineRows = Array.isArray(contract?.lineRows) ? contract.lineRows : [];

          if (lineRows.length > 0) {
            lineRows.forEach((lineRow: any) => {
              const projectId = Number(lineRow?.projectId || 0);
              if (!projectIds.has(projectId)) return;

              const realFundingId = Number(lineRow?.realFundingId || 0);
              contractLandPayments.forEach((payment: any) => {
                if (Number(payment?.status || 0) !== 2) return;
                if (realFundingId > 0 && Number(payment?.realFundingId || 0) !== realFundingId) {
                  return;
                }
                addAmountByYear(
                  valuesByYear,
                  getYearFromIsoDate(payment?.at),
                  Number(payment?.amountEuro || 0),
                );
              });
            });
            return;
          }

          const fallbackProjectId = Number(contract?.project?.id || 0);
          if (!projectIds.has(fallbackProjectId)) return;

          contractLandPayments.forEach((payment: any) => {
            if (Number(payment?.status || 0) !== 2) return;
            addAmountByYear(
              valuesByYear,
              getYearFromIsoDate(payment?.at),
              Number(payment?.amountEuro || 0),
            );
          });
        });
      });

      return {
        id: String(subcontractor.id),
        label: subcontractor.name,
        valuesByYear,
        total: Number(
          Object.values(valuesByYear).reduce((sum, amount) => sum + Number(amount || 0), 0).toFixed(2),
        ),
      } satisfies DashboardYearMatrixRow;
    })
    .sort((a, b) => a.label.localeCompare(b.label, "fr"));

  return finalizeDashboardYearMatrix(rows);
};

function AnnualBreakdownTable({
  title,
  description,
  rowLabel,
  data,
  loading = false,
  emptyMessage,
  defaultOpen = false,
}: {
  title: string;
  description: string;
  rowLabel: string;
  data: DashboardYearMatrixData;
  loading?: boolean;
  emptyMessage: string;
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "common",
  });
  const { t: tDashboard } = useTranslation("dashboard");
  const [isOpen, setIsOpen] = useState(() => {
    return defaultOpen;
  });
  const tableWidthPx = 420 + data.years.length * 140;

  return (
    <div className="rounded-xl bg-card p-5">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-start justify-between gap-3 text-left"
      >
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            {tDashboard("common.Vue annuelle")}
          </div>
          <div className="mt-1 text-sm font-medium text-slate-900">{title}</div>
          <div className="mt-1 text-xs text-slate-500">{description}</div>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
          {isOpen
            ? tDashboard("common.Plier")
            : tDashboard("common.Déplier")}
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`}
          />
        </span>
      </button>

      {isOpen ? (
        <div className="mt-3">
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-11 w-full rounded-lg" />
              ))}
            </div>
          ) : data.years.length === 0 || data.rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
              {emptyMessage}
            </div>
          ) : (
            <CustomHorizontalScroller compact>
              <div
                className="rounded-xl border border-slate-200 bg-white"
                style={{
                  width: `${tableWidthPx}px`,
                  minWidth: "100%",
                }}
              >
                <table
                  className="w-full table-fixed border-separate border-spacing-0"
                >
                  <colgroup>
                    <col style={{ width: "260px" }} />
                    {data.years.map((year) => (
                      <col key={`${title}-col-${year}`} style={{ width: "140px" }} />
                    ))}
                    <col style={{ width: "160px" }} />
                  </colgroup>
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="sticky left-0 z-20 border-b border-r border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-semibold text-slate-600">
                        {rowLabel}
                      </th>
                      {data.years.map((year) => (
                        <th
                          key={`${title}-year-${year}`}
                          className="border-b border-r border-slate-200 bg-slate-50 px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap"
                        >
                          {year}
                        </th>
                      ))}
                      <th className="sticky right-0 z-20 border-b border-l border-slate-200 bg-slate-50 px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, index) => (
                      <tr key={`${title}-row-${row.id}-${index}`}>
                        <td
                          className={`sticky left-0 z-10 border-b border-r border-slate-200 px-3 py-2 text-sm font-medium text-slate-900 ${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          }`}
                        >
                          {row.label}
                        </td>
                        {data.years.map((year) => (
                          <td
                            key={`${title}-${row.id}-${year}`}
                            className={`border-b border-r border-slate-200 px-3 py-2 text-right text-sm text-slate-700 whitespace-nowrap ${
                              index % 2 === 0 ? "bg-white" : "bg-slate-50"
                            }`}
                          >
                            {formatEuro(row.valuesByYear[year] || 0)}
                          </td>
                        ))}
                        <td
                          className={`sticky right-0 z-10 border-b border-l border-slate-200 px-3 py-2 text-right text-sm font-semibold text-slate-900 whitespace-nowrap ${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          }`}
                        >
                          {formatEuro(row.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100">
                      <td className="sticky left-0 z-20 border-r border-t border-slate-200 bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-900">
                        Total
                      </td>
                      {data.years.map((year) => (
                        <td
                          key={`${title}-total-${year}`}
                          className="border-r border-t border-slate-200 bg-slate-100 px-3 py-2 text-right text-sm font-semibold text-slate-900 whitespace-nowrap"
                        >
                          {formatEuro(data.columnTotals[year] || 0)}
                        </td>
                      ))}
                      <td className="sticky right-0 z-20 border-l border-t border-slate-200 bg-slate-100 px-3 py-2 text-right text-sm font-semibold text-slate-900 whitespace-nowrap">
                        {formatEuro(data.grandTotal)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CustomHorizontalScroller>
          )}
        </div>
      ) : null}
    </div>
  );
}

function CustomHorizontalScroller({
  children,
  compact = false,
}: {
  children: ReactNode;
  compact?: boolean;
}) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startClientX: number;
    startScrollLeft: number;
  } | null>(null);
  const [metrics, setMetrics] = useState({
    viewportWidth: 0,
    contentWidth: 0,
    maxScrollLeft: 0,
  });
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const viewportNode = viewportRef.current;
    const contentNode = contentRef.current;
    if (!viewportNode || !contentNode) return;

    const updateMetrics = () => {
      const viewportWidth = viewportNode.clientWidth;
      const contentWidth = contentNode.scrollWidth;
      const maxScrollLeft = Math.max(0, contentWidth - viewportWidth);

      setMetrics({
        viewportWidth,
        contentWidth,
        maxScrollLeft,
      });
      setScrollLeft((current) => Math.min(current, maxScrollLeft));
    };

    updateMetrics();

    const observer = new ResizeObserver(() => {
      updateMetrics();
    });
    observer.observe(viewportNode);
    observer.observe(contentNode);

    return () => {
      observer.disconnect();
    };
  }, [children]);

  const syncScrollLeft = (nextScrollLeft: number) => {
    const viewportNode = viewportRef.current;
    const maxScrollLeft = metrics.maxScrollLeft;
    const clampedValue = Math.max(0, Math.min(nextScrollLeft, maxScrollLeft));

    if (viewportNode) {
      viewportNode.scrollLeft = clampedValue;
    }
    setScrollLeft(clampedValue);
  };

  const handleViewportScroll = () => {
    const viewportNode = viewportRef.current;
    if (!viewportNode) return;
    setScrollLeft(viewportNode.scrollLeft);
  };

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const trackNode = trackRef.current;
    if (!trackNode || metrics.maxScrollLeft <= 0) return;

    const bounds = trackNode.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (event.clientX - bounds.left) / bounds.width),
    );
    syncScrollLeft(ratio * metrics.maxScrollLeft);
  };

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (metrics.maxScrollLeft <= 0) return;
    dragStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startScrollLeft: scrollLeft,
    };

    const releaseDrag = () => {
      dragStateRef.current = null;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", releaseDrag);
      window.removeEventListener("pointercancel", releaseDrag);
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const state = dragStateRef.current;
      const trackNode = trackRef.current;
      if (!state || !trackNode) return;
      if (moveEvent.pointerId !== state.pointerId) return;

      const deltaX = moveEvent.clientX - state.startClientX;
      const trackWidth = trackNode.clientWidth;
      if (trackWidth <= 0) return;

      const nextScrollLeft =
        state.startScrollLeft + (deltaX / trackWidth) * metrics.maxScrollLeft;
      syncScrollLeft(nextScrollLeft);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", releaseDrag);
    window.addEventListener("pointercancel", releaseDrag);
  };

  const thumbWidthPercent =
    metrics.contentWidth > 0
      ? Math.max(
          12,
          Math.min(100, (metrics.viewportWidth / metrics.contentWidth) * 100),
        )
      : 100;
  const thumbTravelPercent = Math.max(0, 100 - thumbWidthPercent);
  const thumbLeftPercent =
    metrics.maxScrollLeft > 0
      ? (scrollLeft / metrics.maxScrollLeft) * thumbTravelPercent
      : 0;

  return (
    <div className="min-w-0 w-full max-w-full">
      <div
        ref={viewportRef}
        className={cn(
          "hide-native-scrollbar min-w-0 w-full max-w-full overflow-x-auto",
          compact ? "pb-1.5" : "pb-2",
        )}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        onScroll={handleViewportScroll}
      >
        <div ref={contentRef} className="inline-block min-w-full align-top">
          {children}
        </div>
      </div>

      {metrics.maxScrollLeft > 0 ? (
        <div className={cn(compact ? "px-0.5" : "px-1")}>
          <div
            ref={trackRef}
            className={cn(
              "relative w-full cursor-pointer rounded-full bg-slate-200/90",
              compact ? "h-2.5" : "h-3",
            )}
            onPointerDown={handleTrackPointerDown}
          >
            <div
              role="slider"
              aria-label="Défilement horizontal"
              aria-valuemin={0}
              aria-valuemax={Math.round(metrics.maxScrollLeft)}
              aria-valuenow={Math.round(scrollLeft)}
              tabIndex={0}
              onPointerDown={handleThumbPointerDown}
              className={cn(
                "absolute rounded-full bg-slate-400 shadow-sm transition-colors hover:bg-slate-500",
                compact ? "top-[2px] h-[6px]" : "top-[2px] h-[8px]",
              )}
              style={{
                left: `${thumbLeftPercent}%`,
                width: `${thumbWidthPercent}%`,
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FundingOverviewStatCard({
  icon,
  label,
  value,
  loading = false,
  tone = "slate",
  subtitle,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  loading?: boolean;
  tone?: SynthesesMetricTone;
  subtitle?: string;
}) {
  const toneClasses = getSynthesesMetricToneClasses(tone);
  const safeSubtitle = toTrimmedText(subtitle);

  return (
    <div
      className={`rounded-xl border p-3 shadow-[0_1px_0_rgba(15,23,42,0.03)] ${toneClasses.card}`}
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-black/5 ${toneClasses.iconWrap}`}
        >
          {icon}
        </span>
        <span className={`text-xs uppercase tracking-wide ${toneClasses.label}`}>
          {label}
        </span>
      </div>
      {loading ? (
        <Skeleton className="h-7 w-24" />
      ) : (
        <div className={`text-xl font-semibold ${toneClasses.value}`}>
          {value}
        </div>
      )}
      {safeSubtitle ? (
        <div className={`mt-1 text-[11px] ${toneClasses.meta}`}>
          {safeSubtitle}
        </div>
      ) : null}
    </div>
  );
}

// Programm Carousel
const DashboardExportDialog = ({
  isOpen,
  onOpenChange,
  onExport,
  isExporting,
  selectedFormat,
  onSelectFormat,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (format: "xlsx" | "docx") => Promise<void>;
  isExporting: boolean;
  selectedFormat: "xlsx" | "docx";
  onSelectFormat: (format: "xlsx" | "docx") => void;
}) => (
  <Dialog open={isOpen} onOpenChange={onOpenChange}>
    <DialogContent className="w-full min-w-0 max-w-[720px] rounded-[28px] border-[#d6e2d2] bg-[linear-gradient(180deg,#fbfdf9_0%,#f2f7ef_100%)] p-0 shadow-2xl sm:min-w-0">
      <div className="overflow-hidden rounded-[28px]">
        <div className="bg-[linear-gradient(135deg,#1f4b1d_0%,#5f8e53_60%,#94bf85_100%)] px-8 py-7 text-white">
          <div className="flex items-start justify-between gap-5">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
                <Download className="h-3.5 w-3.5" />
                Rapport dashboard
              </div>
              <DialogTitle className="mt-4 text-[28px] font-semibold leading-tight text-white">
                Exporter le tableau de bord
              </DialogTitle>
              <DialogDescription className="mt-2 max-w-[560px] text-sm leading-6 text-white/85">
                Export du dashboard global en Excel ou en Word avec les principales sections visibles dans le web.
              </DialogDescription>
            </div>
            <div className="hidden rounded-[24px] border border-white/15 bg-white/10 p-4 md:block">
              {selectedFormat === "xlsx" ? (
                <FileSpreadsheet className="h-10 w-10 text-white" />
              ) : (
                <FileText className="h-10 w-10 text-white" />
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-5 px-8 py-7 md:grid-cols-2">
          <button
            type="button"
            onClick={() => onSelectFormat("xlsx")}
            className={cn(
              "relative overflow-hidden rounded-[24px] border bg-white p-5 text-left shadow-[0_18px_50px_rgba(75,107,60,0.15)] transition-all",
              selectedFormat === "xlsx"
                ? "border-[#6f9a5c] ring-2 ring-[#dfead9]"
                : "border-[#d7e5d2] hover:border-[#bfd2b7]",
            )}
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#2e5f22_0%,#8fb37f_100%)]" />
            <div className="w-fit rounded-[18px] bg-[#edf6e9] p-3 text-[#2a5b1e]">
              <IconExcel className="h-8 w-8" />
            </div>
            <h3 className="mt-5 text-lg font-semibold text-[#24451f]">Excel</h3>
            <p className="mt-2 text-sm leading-6 text-[#5d6e58]">
              Export complet avec tableaux, synthèses, indicateurs et sections du dashboard.
            </p>
          </button>
          <button
            type="button"
            onClick={() => onSelectFormat("docx")}
            className={cn(
              "relative overflow-hidden rounded-[24px] border bg-white p-5 text-left shadow-[0_18px_50px_rgba(75,107,60,0.15)] transition-all",
              selectedFormat === "docx"
                ? "border-[#6f9a5c] ring-2 ring-[#dfead9]"
                : "border-[#d7e5d2] hover:border-[#bfd2b7]",
            )}
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#29586a_0%,#8eb3c0_100%)]" />
            <div className="w-fit rounded-[18px] bg-[#edf4f7] p-3 text-[#29586a]">
              <FileText className="h-8 w-8" />
            </div>
            <h3 className="mt-5 text-lg font-semibold text-[#24451f]">Word</h3>
            <p className="mt-2 text-sm leading-6 text-[#5d6e58]">
              Rapport simplifié présentant les informations clés sous forme de texte, tableaux et synthèses.
            </p>
          </button>
        </div>

        <DialogFooter className="gap-3 border-t border-[#dde7d8] bg-white/80 px-8 py-5 sm:justify-between sm:gap-3">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl border-[#cfdcc9] bg-white px-5 text-[#496045] hover:bg-[#f4f8f2]"
            onClick={() => onOpenChange(false)}
          >
            Annuler
          </Button>
          <Button
            type="button"
            className="rounded-xl bg-[linear-gradient(135deg,#21511c_0%,#5f8f51_100%)] px-6 text-white shadow-lg hover:opacity-95"
            onClick={() => void onExport(selectedFormat)}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Export en cours
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Exporter
              </>
            )}
          </Button>
        </DialogFooter>
      </div>
    </DialogContent>
  </Dialog>
);

export const CarouselProgram = () => {
  const showPrimaryDashboardData = useDeferredActivation(0);
  const showSecondaryDashboardData = useDeferredActivation(120);
  const showExportPayloadData = useDeferredActivation(900);
  const [graphsSectionRef, showGraphsSection] = useViewportActivation("700px");
  const [globalInsightsSectionRef, showGlobalInsightsSection] =
    useViewportActivation("700px");
  const apiBase = String(import.meta.env.VITE_API_ENDPOINT || "").trim().replace(/\/+$/, "");
  const { data, loading, error } = useFetchDataV2(
    `${import.meta.env.VITE_API_ENDPOINT}/info/programs`,
    {
      browserCache: true,
      browserCacheKey: "programsInfo",
      browserCacheNamespace: "dashboard",
      browserCacheTtlMs: 1000 * 60 * 60,
      cacheId: dashboardQueryKeys.programsInfo,
      gcTime: DASHBOARD_QUERY_GC_TIME,
      staleTime: DASHBOARD_QUERY_STALE_TIMES.programsInfo,
    },
  );
  const programsInfoLightQuery = useFetchDataV2(
    `${apiBase}/info/programs-light`,
    {
      browserCache: true,
      browserCacheKey: "programsInfoLight",
      browserCacheNamespace: "dashboard",
      browserCacheTtlMs: 1000 * 60 * 60,
      cacheId: "programsInfoLight",
      gcTime: DASHBOARD_QUERY_GC_TIME,
      staleTime: DASHBOARD_QUERY_STALE_TIMES.programsInfo,
    },
  );
  const projectTypeFundingQuery = useFetchDataV2<ProjectTypeFundingInfoResponse>(
    `${apiBase}/info/project-type-funding`,
    {
      browserCache: true,
      browserCacheKey: "projectTypeFunding",
      browserCacheNamespace: "dashboard",
      browserCacheTtlMs: 1000 * 60 * 60,
      cacheId: "projectTypeFunding",
      gcTime: DASHBOARD_QUERY_GC_TIME,
      staleTime: DASHBOARD_QUERY_STALE_TIMES.programsInfo,
    },
  );

  const programReport = useFetchDataV2(
    showPrimaryDashboardData
      ? `${import.meta.env.VITE_API_ENDPOINT}/reporting?context=dashboard`
      : "",
    {
      browserCache: true,
      browserCacheKey: "dashboard-reporting",
      browserCacheNamespace: "dashboard",
      browserCacheTtlMs: 1000 * 60 * 30,
      cacheId: dashboardQueryKeys.reporting,
      gcTime: DASHBOARD_QUERY_GC_TIME,
      staleTime: DASHBOARD_QUERY_STALE_TIMES.reporting,
    },
  );
  const { data: pendingVerdeoProjectsData } = useFetchDataV2(
    showSecondaryDashboardData
      ? `${import.meta.env.VITE_API_ENDPOINT}/real-fundings?by=project&includeVerdeoPaid=1&pendingVerdeoOnly=1`
      : "",
    {
      browserCache: true,
      browserCacheKey: "pending-verdeo-projects",
      browserCacheNamespace: "dashboard",
      browserCacheTtlMs: 1000 * 60 * 10,
      cacheId: dashboardQueryKeys.pendingVerdeoFundings,
      gcTime: DASHBOARD_QUERY_GC_TIME,
      staleTime: DASHBOARD_QUERY_STALE_TIMES.pendingVerdeoFundings,
    },
  );
  const activeProjectsQuery = useFetchDataV2<any>(
    showGlobalInsightsSection || showExportPayloadData
      ? `${import.meta.env.VITE_API_ENDPOINT}/AaP/projects`
      : "",
    dashboardQueryKeys.listingProjects,
    DASHBOARD_QUERY_STALE_TIMES.listingProjects,
    DASHBOARD_QUERY_GC_TIME,
  );
  const subcontractorsQuery = useFetchDataV2<any>(
    showGlobalInsightsSection || showExportPayloadData
      ? `${import.meta.env.VITE_API_ENDPOINT}/users/subcontractors`
      : "",
    {
      browserCache: true,
      browserCacheKey: "subcontractors",
      browserCacheNamespace: "dashboard",
      browserCacheTtlMs: 1000 * 60 * 30,
      cacheId: dashboardQueryKeys.subcontractors,
      gcTime: DASHBOARD_QUERY_GC_TIME,
      staleTime: DASHBOARD_QUERY_STALE_TIMES.subcontractors,
    },
  );
  const accessQuery = useFetchDataV2<Record<string, DashboardAccessProgramResponse>>(
    "",
    {
      browserCache: true,
      browserCacheNamespace: "dashboard-access",
      gcTime: DASHBOARD_QUERY_GC_TIME,
      staleTime: DASHBOARD_QUERY_STALE_TIMES.subcontractorAccess,
    },
  );
  const fetchIntervenantAccessData = accessQuery.fetchData;
  const groupedContractsQuery = useSynthesesGroupedContractsRead("financeurs", {
    cacheId: dashboardQueryKeys.groupedContracts("financeurs"),
    enabled: showSecondaryDashboardData,
    gcTime: DASHBOARD_QUERY_GC_TIME,
    staleTime: DASHBOARD_QUERY_STALE_TIMES.groupedContracts,
  });
  const { t } = useTranslation("dashboard", {
    keyPrefix: "common",
  });
  const { t: tDashboard } = useTranslation("dashboard");
  const [isDashboardExportDialogOpen, setIsDashboardExportDialogOpen] =
    useState(false);
  const [isDashboardExporting, setIsDashboardExporting] = useState(false);
  const [isDashboardChartsOpen, setIsDashboardChartsOpen] = useState(false);
  const [dashboardExportFormat, setDashboardExportFormat] = useState<
    "xlsx" | "docx"
  >("xlsx");

  const programs = useMemo(() => normalizeDashboardPrograms(data), [data]);
  const carouselPrograms = useMemo(
    () => {
      const lightPrograms = normalizeDashboardPrograms(programsInfoLightQuery.data);

      return lightPrograms.length > 0 ? lightPrograms : programs;
    },
    [programs, programsInfoLightQuery.data],
  );
  const reportData = programReport.data;
  const reportLoading = !showPrimaryDashboardData || programReport.loading;
  const fallbackFundingSummary = useMemo(() => {
    const funding: [number, number] = [0, 0];
    const fundingByTypeMap = new Map<number, {
      type: any;
      total: number;
      obtained: number;
    }>();

    programs.forEach((program: any) => {
      const projects = Array.isArray(program?.projects) ? program.projects : [];

      projects.forEach((project: any) => {
        const desiredFunding = Array.isArray(project?.desiredFunding)
          ? project.desiredFunding
          : [];
        const obtainedFunding = Array.isArray(project?.obtainedFunding)
          ? project.obtainedFunding
          : [];

        const desiredTotal = desiredFunding.reduce(
          (sum: number, item: any) => sum + readFundingAmountEuro(item),
          0,
        );
        const obtainedTotal = obtainedFunding.reduce(
          (sum: number, item: any) => sum + readFundingAmountEuro(item),
          0,
        );

        funding[0] += desiredTotal;
        funding[1] += obtainedTotal;

        const typeId = Number(project?.type?.id || 0);
        if (!Number.isFinite(typeId) || typeId <= 0) {
          return;
        }

        if (!fundingByTypeMap.has(typeId)) {
          fundingByTypeMap.set(typeId, {
            type: project.type,
            total: 0,
            obtained: 0,
          });
        }

        const entry = fundingByTypeMap.get(typeId);
        if (!entry) return;

        entry.total += desiredTotal + obtainedTotal;
        entry.obtained += desiredTotal + obtainedTotal;
      });
    });

    return {
      totalFunding: funding,
      fundingByType: Array.from(fundingByTypeMap.values()),
    };
  }, [programs]);
  const { totalFunding, fundingByType } = useMemo(() => {
    const apiTotalFunding = Array.isArray(projectTypeFundingQuery.data?.totalFunding)
      ? projectTypeFundingQuery.data.totalFunding
      : null;
    const apiFundingByType = Array.isArray(projectTypeFundingQuery.data?.fundingByType)
      ? projectTypeFundingQuery.data.fundingByType
      : null;

    if (apiTotalFunding && apiFundingByType) {
      return {
        totalFunding: [
          Number(apiTotalFunding[0] || 0),
          Number(apiTotalFunding[1] || 0),
        ] as [number, number],
        fundingByType: apiFundingByType,
      };
    }

    return fallbackFundingSummary;
  }, [fallbackFundingSummary, projectTypeFundingQuery.data]);
  const subcontractors = useMemo(
    () => buildDashboardSubcontractors(subcontractorsQuery.data),
    [subcontractorsQuery.data],
  );
  const accessMemberIds = useMemo(
    () =>
      normalizeUserAccessMemberIds(
        subcontractors.flatMap((subcontractor) => subcontractor.memberIds),
      ),
    [subcontractors],
  );
  const accessMemberIdsKey = useMemo(
    () => accessMemberIds.join(","),
    [accessMemberIds],
  );
  const pendingVerdeoAllocationWarnings = useMemo(
    () => collectPendingVerdeoAllocationWarnings(pendingVerdeoProjectsData),
    [pendingVerdeoProjectsData],
  );
  const firstPendingWarningHref = useMemo(() => {
    const firstWarning = pendingVerdeoAllocationWarnings[0] || null;
    if (!firstWarning) {
      return "/dashboard/syntheses/financeurs";
    }

    return buildPendingVerdeoSynthesesHref({
      projectId: firstWarning.projectId,
      projectName: firstWarning.projectName,
      financeurs: groupedContractsQuery.data?.items || [],
    });
  }, [groupedContractsQuery.data?.items, pendingVerdeoAllocationWarnings]);
  const currentUserStructureName = useMemo(() => getCurrentUserStructureName(), []);
  const currentUserId = useMemo(() => getCurrentUserDashboardId(), []);
  const currentLocale = useMemo(
    () => localStorage.getItem("i18nextLng") || i18n.language || "fr",
    [],
  );
  const structureShareLabel = currentUserStructureName
    ? `Part ${currentUserStructureName}`
    : "Part de votre structure";
  const fundingOverview = useMemo(() => {
    const items = Array.isArray(groupedContractsQuery.data?.items)
      ? groupedContractsQuery.data.items
      : [];
    const seenContractKeys = new Set<string>();

    const summary = items.reduce(
      (acc, item) => {
        const contracts = Array.isArray(item?.contracts) ? item.contracts : [];
        contracts.forEach((contract) => {
          const contractKey =
            String(contract?.id || "").trim() ||
            String(contract?.synthesesContractId || "").trim() ||
            [
              contract?.sourceType || "",
              contract?.realFundingId || "",
              contract?.contractDate || "",
              contract?.title || "",
            ].join("|");

          if (contractKey && !seenContractKeys.has(contractKey)) {
            seenContractKeys.add(contractKey);
            acc.totalContractsEuro += Number(contract?.amountEuro || 0);
          }

          const landPayments = Array.isArray(contract?.landPayments)
            ? contract.landPayments
            : [];
          acc.terrainPaymentsEuro += landPayments.reduce((sum, payment) => {
            return Number(payment?.status || 0) === 2
              ? sum + Number(payment?.amountEuro || 0)
              : sum;
          }, 0);
        });

        return acc;
      },
      { totalContractsEuro: 0, terrainPaymentsEuro: 0 },
    );

    const totalContractsEuro = Number(summary.totalContractsEuro.toFixed(2));
    const terrainPaymentsEuro = Number(summary.terrainPaymentsEuro.toFixed(2));

    return {
      totalContractsEuro,
      terrainPaymentsEuro,
      structureShareEuro: Number(
        (totalContractsEuro - terrainPaymentsEuro).toFixed(2),
      ),
    };
  }, [groupedContractsQuery.data?.items]);
  const fundingOverviewLoading =
    groupedContractsQuery.loading || groupedContractsQuery.isFetching;
  const financeurYearMatrix = useMemo(
    () => buildFinanceurYearMatrix(groupedContractsQuery.data?.items || []),
    [groupedContractsQuery.data?.items],
  );
  const intervenantAccessQuery = useQuery<
    Record<string, DashboardAccessProgramResponse>,
    Error
  >({
    enabled:
      showSecondaryDashboardData && subcontractors.length > 0 && apiBase.length > 0,
    gcTime: DASHBOARD_QUERY_GC_TIME,
    queryFn: async () => {
      const globalBatchEndpoints = buildUserAccessBatchEndpoints(
        apiBase,
        accessMemberIds,
      );
      const globalPayloads = await Promise.all(
        globalBatchEndpoints.map((endpoint) => fetchIntervenantAccessData(endpoint)),
      );
      let payload = mergeBatchAccessPayloads(globalPayloads);

      if (
        accessMemberIds.length > 0 &&
        Object.keys(payload).length === 0
      ) {
        const fallbackPayloads = await Promise.all(
          subcontractors.map(async (subcontractor) => {
            const endpoint = buildUserAccessBatchEndpoint(
              apiBase,
              subcontractor.memberIds,
            );

            return endpoint
              ? await fetchIntervenantAccessData(endpoint)
              : null;
          }),
        );
        payload = mergeBatchAccessPayloads(fallbackPayloads);
      }

      return Object.fromEntries(
        subcontractors.map((subcontractor) => {
          const responses = readBatchAccessResponses(
            payload,
            subcontractor.memberIds,
          );

          return [
            String(subcontractor.id),
            mergeDashboardAccessProgramResponses(responses),
          ] as const;
        }),
      );
    },
    queryKey: dashboardQueryKeys.subcontractorAccess(
      accessMemberIdsKey || "empty",
    ),
    refetchOnWindowFocus: false,
    retry: 0,
    staleTime: DASHBOARD_QUERY_STALE_TIMES.subcontractorAccess,
  });
  const intervenantAccessBySubcontractorId =
    intervenantAccessQuery.data || {};
  const intervenantYearMatrix = useMemo(
    () =>
      buildIntervenantYearMatrix(
        Array.isArray(groupedContractsQuery.data?.items)
          ? groupedContractsQuery.data.items
          : [],
        subcontractors,
        intervenantAccessBySubcontractorId,
      ),
    [groupedContractsQuery.data?.items, intervenantAccessBySubcontractorId, subcontractors],
  );
  const intervenantMatrixLoading =
    fundingOverviewLoading ||
    subcontractorsQuery.loading ||
    subcontractorsQuery.isFetching ||
    intervenantAccessQuery.isLoading ||
    intervenantAccessQuery.isFetching;

  const dashboardExportPayload = useMemo(() => {
    if (!showExportPayloadData && !isDashboardExportDialogOpen) {
      return null;
    }

    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    const normalizedPrograms = Array.isArray(programs) ? programs : [];
    const reportIndicators = Array.isArray(reportData?.projectTotalIndicators)
      ? reportData.projectTotalIndicators
      : [];
    const dashboardIndicators = Array.isArray(reportData?.indicators)
      ? reportData.indicators
      : [];
    const projectIndicators = Array.isArray(reportData?.projectIndicators)
      ? reportData.projectIndicators
      : [];
    const activeProjects = Array.isArray(activeProjectsQuery.data)
      ? activeProjectsQuery.data
      : [];
    const indicatorColorPalette = [
      { accent: "F28C28", soft: "FDE7D3" },
      { accent: "3B82F6", soft: "DBEAFE" },
      { accent: "6B7280", soft: "E5E7EB" },
      { accent: "EF4444", soft: "FEE2E2" },
      { accent: "16A34A", soft: "DCFCE7" },
    ];
    const localizedIndicatorName = (indicator: any) =>
      indicator?.indicatorLangs?.find((entry: any) => entry?.lang?.code === i18n.language)
        ?.name ||
      indicator?.name ||
      "-";

    const projectCount = normalizedPrograms.reduce((sum, program) => {
      const programProjects = Array.isArray(program?.projects)
        ? program.projects
        : [];
      return sum + programProjects.length;
    }, 0);

    const indicatorCards = Array.from(
      new Map(
        reportIndicators
          .filter((entry: any) => entry?.indicator?.code !== "euro_recus")
          .map((entry: any) => [entry?.indicator?.id, entry]),
      ).values(),
    ).map((entry: any, index: number) => {
      const plannedIndicator = reportIndicators.find(
        (item: any) =>
          item?.indicator?.id === entry?.indicator?.id && Number(item?.type) === 1,
      );
      const realizedIndicator = reportIndicators.find(
        (item: any) =>
          item?.indicator?.id === entry?.indicator?.id && Number(item?.type) === 2,
      );
      const plannedValue =
        entry?.indicator?.moyenne && plannedIndicator?.count
          ? Number(plannedIndicator?.total || 0) / Number(plannedIndicator?.count || 1)
          : Number(plannedIndicator?.total || 0);
      const realizedValue =
        entry?.indicator?.moyenne && realizedIndicator?.count
          ? Number(realizedIndicator?.total || 0) /
            Number(realizedIndicator?.count || 1)
          : Number(realizedIndicator?.total || 0);
      const palette =
        indicatorColorPalette[(index + 1) % indicatorColorPalette.length];
      const unit = formatIndicatorImpactUnitLabel(plannedIndicator?.indicator?.unity);

      return {
        title: localizedIndicatorName(entry?.indicator),
        value: Math.ceil(plannedValue),
        description: `Déjà réalisé : ${Math.ceil(realizedValue).toLocaleString("fr-FR")} ${unit || ""}`.trim(),
        unit,
        accentColor: palette.accent,
        backgroundColor: palette.soft,
      };
    });

    const euroIndicator = reportIndicators.find(
      (entry: any) => entry?.indicator?.code === "euro_recus",
    );
    if (euroIndicator) {
      const roundedRealizedFunding = Math.ceil(Number(totalFunding[0] || 0));
      indicatorCards.unshift({
        title: "Coût total prévu",
        value: Math.ceil(Number(totalFunding[0] || 0) + Number(totalFunding[1] || 0)),
        description: `Déjà réalisé : ${Number(totalFunding[0] || 0).toFixed(1)} €`,
        unit: "€",
        accentColor: indicatorColorPalette[0].accent,
        backgroundColor: indicatorColorPalette[0].soft,
      });
      indicatorCards[0].description = [
        "DÃ©jÃ  rÃ©alisÃ© :",
        roundedRealizedFunding.toLocaleString("fr-FR"),
        "â‚¬",
      ].join(" ");
    }

    const charts = dashboardIndicators
      .filter((indicator: any) => {
        const hasBlocking =
          indicator?.prog_id === null &&
          indicator?.programme === false &&
          indicator?.programReporting === null;

        return (
          Number(indicator?.id) !== 2 &&
          !hasBlocking &&
          indicator?.dashboard === true &&
          indicator?.prog_id === null
        );
      })
      .map((indicator: any, index: number) => {
        const palette = indicatorColorPalette[index % indicatorColorPalette.length];
        const rows = getChartDataYear(
          projectIndicators,
          indicator,
          reportData?.maxDate
            ? Number(reportData.maxDate) - Number(reportData.minDate) + 1
            : 1,
          reportData?.minDate ? Number(reportData.minDate) : new Date().getFullYear(),
          reportData?.minDate ? Number(reportData.minDate) : null,
          reportData?.maxDate ? Number(reportData.maxDate) : null,
          "dashboard",
        );

        return {
          title: localizedIndicatorName(indicator),
          unit: formatIndicatorUnitLabel(indicator?.unity),
          cumulativeLabel: Boolean(
            indicator?.cumulativeDashboard ?? indicator?.cumulative,
          )
              ? "Cumulatif"
              : "Non cumulatif",
          accentColor: palette.accent,
          backgroundColor: palette.soft,
          rows: rows.map((row: any) => ({
            legend: String(row?.legend ?? "-"),
            prevu: Number(row?.prevu || 0),
            realise: Number(row?.realise || 0),
            verifie: Number(row?.verifie || 0),
          })),
        };
      });

    return {
      programs: normalizedPrograms.map((program: any) => ({
        programId: Number(program?.id || 0),
        programName: String(program?.name ?? "-"),
        programDescription: String(program?.description ?? "").trim(),
        projectCount: Array.isArray(program?.projects) ? program.projects.length : 0,
        programCoverUri:
          typeof program?.cover?.uri === "string" && program.cover.uri.trim() !== ""
            ? program.cover.uri
            : typeof program?.defaultImage?.uri === "string" &&
                program.defaultImage.uri.trim() !== ""
              ? program.defaultImage.uri
              : null,
        programUrl:
          Number(program?.id || 0) > 0
            ? `${origin}/dashboard/program/${Number(program.id)}`
            : "-",
      })),
      fundingOverview: {
        totalContractsEuro: Number(fundingOverview.totalContractsEuro || 0),
        totalContractsSubtitle: "Tous contrats synthèse",
        terrainPaymentsEuro: Number(fundingOverview.terrainPaymentsEuro || 0),
        terrainPaymentsSubtitle: "Versements réalisés",
        structureShareLabel,
        structureShareEuro: Number(fundingOverview.structureShareEuro || 0),
        structureShareSubtitle: "Contrats moins versements réalisés",
      },
      financeurAnnual: financeurYearMatrix,
      intervenantAnnual: intervenantYearMatrix,
      projectFunding: {
        desiredEuro: Number(totalFunding[0] || 0),
        totalEuro: Number(totalFunding[0] || 0) + Number(totalFunding[1] || 0),
      },
      fundingByType: (Array.isArray(fundingByType) ? fundingByType : []).map(
        (entry: any) => ({
          title:
            entry?.type?.projectTypeLangs?.find(
              (item: any) => item?.lang?.code === i18n.language,
            )?.name ||
            entry?.type?.name ||
            "Personnalisé",
          total: Number(entry?.total || 0),
        }),
      ),
      indicatorCards,
      activeProjectSections: buildDashboardActiveProjectSections({
        projects: activeProjects,
        currentUserId,
        locale: currentLocale,
        labels: {
          awaitingYourValidation: "En attente de votre validation",
          awaitingVerdeoValidation: "En attente de validation Verdeo",
          inProgress: "En cours",
          me: "Moi-même",
          creators: "Créateur",
          subcontractors: "Intervenants",
          approvalRequest: "Demande d'approbation",
          creationDate: "Date de création",
          waitingDuration: "Durée d'attente",
          sinceWhen: "Depuis quand ?",
        },
      }),
      charts,
    };
  }, [
    activeProjectsQuery.data,
    currentLocale,
    currentUserId,
    financeurYearMatrix,
    fundingByType,
    fundingOverview,
    i18n.language,
    intervenantYearMatrix,
    programs,
    reportData,
    showExportPayloadData,
    isDashboardExportDialogOpen,
    totalFunding,
    structureShareLabel,
  ]);

  const handleDashboardExport = async (format: "xlsx" | "docx") => {
    try {
      if (!dashboardExportPayload) {
        toast({
          title: "Export en préparation",
          description: "Les données du rapport sont encore en cours de chargement.",
        });
        return;
      }

      setIsDashboardExporting(true);
      if (format === "docx") {
        await downloadDashboardWordExport(dashboardExportPayload);
      } else {
        await downloadDashboardExcelExport(dashboardExportPayload);
      }
      setIsDashboardExportDialogOpen(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Export impossible",
        description:
          err instanceof Error
            ? err.message
            : "Une erreur est survenue lors de l'export du dashboard.",
      });
    } finally {
      setIsDashboardExporting(false);
    }
  };

  // if (loading) return < />;
  if (error)
    return (
      <p className="text-red-500">
        {t("Une erreur est survenue :")} {error}
      </p>
    );
  if (!programs)
    return <p className="text-gray-500">{t("Aucun projet trouvé.")}</p>;
  return (
    <div>
      <div className="mx-5 mb-4 flex justify-end">
        <Button
          type="button"
          className="gap-2"
          onClick={() => setIsDashboardExportDialogOpen(true)}
          disabled={
            isDashboardExporting ||
            !dashboardExportPayload ||
            loading ||
            reportLoading ||
            fundingOverviewLoading ||
            activeProjectsQuery.loading
          }
        >
          {isDashboardExporting ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Exporter
        </Button>
      </div>

      <DashboardExportDialog
        isOpen={isDashboardExportDialogOpen}
        onOpenChange={setIsDashboardExportDialogOpen}
        onExport={(format) => handleDashboardExport(format)}
        isExporting={isDashboardExporting}
        selectedFormat={dashboardExportFormat}
        onSelectFormat={setDashboardExportFormat}
      />
      {programsInfoLightQuery.loading && carouselPrograms.length === 0 ? (
        <CarouselSkeleton />
      ) : carouselPrograms.length == 0 ? (
        <div className=" m-5 md:w-1/5 lg:w-1/6 xl:w-[13%]">
          <div className="p-1 group relative flex aspect-[255/200]  overflow-hidden rounded-[15px] bg-gray-200"></div>
        </div>
      ) : (
        <Carousel
          opts={{
            align: "start",
          }}
          className="m-5 w-auto"
        >
          <CarouselContent>
            {carouselPrograms.length > 0 &&
              carouselPrograms.map((program: ProgramType, key: number) => (
                <Suspense
                  key={key}
                  fallback={
                    <Skeleton className="group relative flex aspect-[255/200]  overflow-hidden rounded-[15px] bg-foreground" />
                  }
                >
                  <CarouselItem
                    key={key}
                    className="md:basis-1/5 lg:basis-1/6 xl:basis-[13%]"
                  >
                    <div className="p-1">
                      <LazyProgramCard program={program} />
                    </div>
                  </CarouselItem>
                </Suspense>
              ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      )}

      <div className="mx-5">
        <ListingProject />
      </div>

      {showSecondaryDashboardData &&
        pendingVerdeoAllocationWarnings.length > 0 && (
          <div className="mx-5 mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-amber-900">
                  <AlertTriangle className="h-4 w-4" />
                  Répartition de contrats Verdeo en attente
                </div>
                <p className="mt-1 text-xs text-amber-900/90">
                  Certains contrats Verdeo ne sont pas encore entièrement
                  affectés aux FS. Finalisez la répartition pour garantir la
                  traçabilité des montants.
                </p>
              </div>

              <a
                href={firstPendingWarningHref}
                className="inline-flex items-center rounded-md border border-amber-700 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
              >
                Ouvrir dans Financeurs
              </a>
            </div>

            <div className="mt-3 space-y-2">
              {pendingVerdeoAllocationWarnings.slice(0, 6).map((warning) => (
                <div
                  key={warning.projectId}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-white p-2"
                >
                  <div className="text-xs text-slate-700">
                    <span className="font-semibold">{warning.projectName}</span> :{" "}
                    {warning.pendingContracts} contrat
                    {warning.pendingContracts > 1 ? "s" : ""} à répartir
                    {warning.pendingAmountEur > 0 && (
                      <>
                        {" "}
                        (
                        {warning.pendingAmountEur.toLocaleString("fr-FR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        €)
                      </>
                    )}
                  </div>
                  <a
                    href={buildPendingVerdeoSynthesesHref({
                      projectId: warning.projectId,
                      projectName: warning.projectName,
                      financeurs: groupedContractsQuery.data?.items || [],
                    })}
                    className="text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700"
                  >
                    Répartir maintenant
                  </a>
                </div>
              ))}
              {pendingVerdeoAllocationWarnings.length > 6 && (
                <p className="text-xs text-amber-900/90">
                  +{pendingVerdeoAllocationWarnings.length - 6} autre
                  {pendingVerdeoAllocationWarnings.length - 6 > 1 ? "s" : ""}{" "}
                  projet
                  {pendingVerdeoAllocationWarnings.length - 6 > 1 ? "s" : ""}{" "}
                  concerné
                  {pendingVerdeoAllocationWarnings.length - 6 > 1 ? "s" : ""}.
                </p>
              )}
            </div>
          </div>
        )}

      <div className="flex flex-col md:flex-row">
        <div className=" mr-5 basis-1/2 rounded">
          <div className="rounded-xl bg-card p-5">
            <SliderFinance
              but={Math.ceil(totalFunding[0] + totalFunding[1])}
              money={Math.ceil(totalFunding[0])}
              loading={projectTypeFundingQuery.loading}
            />
          </div>
          <div className="my-5 rounded-xl bg-card p-5" id="repartition-fond">
            <h3 className="p-3"> {t("Coût total selon le type de projet")}</h3>
            <ScrollArea className="h-[210px]  rounded-md ">
              {projectTypeFundingQuery.loading ? (
                <TypeSkeleton />
              ) : (
                <div
                  className={` ${
                    fundingByType && fundingByType.length
                      ? "m-5"
                      : " flex h-[210px]  flex-wrap items-center justify-center"
                  } `}
                >
                  {fundingByType.length
                    ? fundingByType.map((fbt, key) => (
                        <ProjectFinancement
                          key={key}
                          title={
                            fbt.type.projectTypeLangs.find(
                              (ptl) => ptl.lang.code === i18n.language
                            )
                              ? fbt.type.projectTypeLangs.find(
                                  (ptl) => ptl.lang.code === i18n.language
                                )?.name
                              : fbt.type.name
                          }
                          finance={fbt.obtained}
                          total={fbt.total !== 0 ? fbt.total : fbt.obtained}
                          financeTotal={totalFunding}
                        />
                      ))
                    : !projectTypeFundingQuery.loading &&
                      fundingByType.length == 0 && (
                        <p>{t("Aucun projet financé")} </p>
                      )}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
        <div id="data-reporting" className=" md:size-1/2 pb-5">
          <Suspense
            fallback={
              <div className="flex flex-wrap">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div className="basis-1/3 p-2" key={i}>
                    <Skeleton
                      key={i}
                      className="rounded-[15px] w-full h-[100px] bg-[#ecedee]"
                    />
                  </div>
                ))}
              </div>
            }
          >
            <LazyStatsColoredDashboard
              finances={totalFunding}
              programReport={programReport}
              reportData={reportData}
              reportLoading={reportLoading}
            />
          </Suspense>
        </div>
      </div>

      <div
        ref={graphsSectionRef}
        className="mx-5 mb-5 rounded-xl bg-card p-5"
      >
        <button
          type="button"
          onClick={() => setIsDashboardChartsOpen((current) => !current)}
          className="flex w-full items-start justify-between gap-3 text-left"
        >
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
              {tDashboard("common.Tableau de bord visuel")}
            </div>
            <div className="mt-1 text-sm font-medium text-slate-900">
              {tDashboard("common.Graphiques")}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {tDashboard("common.Retrouvez ici les graphiques clés du dashboard.")}
            </div>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
            {isDashboardChartsOpen
              ? tDashboard("common.Plier")
              : tDashboard("common.Déplier")}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isDashboardChartsOpen ? "rotate-0" : "-rotate-90"}`}
            />
          </span>
        </button>

        {isDashboardChartsOpen ? (
          <div className="mt-5">
            {showGraphsSection ? (
              <Suspense
                fallback={<DashboardLargeSectionSkeleton heightClass="h-[420px]" />}
              >
                <LazyChartsDashboard
                  programReport={programReport}
                  reportData={reportData}
                  reportLoading={reportLoading}
                />
              </Suspense>
            ) : (
              <DashboardLargeSectionSkeleton heightClass="h-[420px]" />
            )}
          </div>
        ) : null}
      </div>

      <div ref={globalInsightsSectionRef}>
        {showGlobalInsightsSection ? (
          showSecondaryDashboardData ? (
            <>
              <div className="mx-5 mb-5 rounded-xl bg-card p-5">
                <div className="mb-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    Suivi global
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-900">
                    Contrats et versements à l&apos;intervenant
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <FundingOverviewStatCard
                    icon={<BriefcaseBusiness className="h-4 w-4" />}
                    label="Montant total contrats"
                    value={formatEuro(fundingOverview.totalContractsEuro)}
                    loading={fundingOverviewLoading}
                    tone="teal"
                    subtitle="Tous contrats synthèse"
                  />
                  <FundingOverviewStatCard
                    icon={<CalendarDays className="h-4 w-4" />}
                    label="Versements aux intervenants"
                    value={formatEuro(fundingOverview.terrainPaymentsEuro)}
                    loading={fundingOverviewLoading}
                    tone="olive"
                    subtitle="Versements réalisés"
                  />
                  <FundingOverviewStatCard
                    icon={<BriefcaseBusiness className="h-4 w-4" />}
                    label={structureShareLabel}
                    value={formatEuro(fundingOverview.structureShareEuro)}
                    loading={fundingOverviewLoading}
                    tone="rose"
                    subtitle="Contrats moins versements réalisés"
                  />
                </div>
              </div>

              <div className="mx-5 mb-5 space-y-5">
                <AnnualBreakdownTable
                  title="Contrats par financeur et par année"
                  description="Chaque cellule reprend la part des contrats rattachée à l'année correspondante."
                  rowLabel="Financeur"
                  data={financeurYearMatrix}
                  loading={fundingOverviewLoading}
                  emptyMessage="Aucun contrat synthèse disponible."
                />
                <AnnualBreakdownTable
                  title="Versements aux intervenants par année"
                  description="Chaque ligne reprend les versements réalisés sur les projets affectés à l'organisation."
                  rowLabel="Intervenant"
                  data={intervenantYearMatrix}
                  loading={intervenantMatrixLoading}
                  emptyMessage="Aucun versement à l'intervenant disponible."
                />
              </div>
            </>
          ) : (
            <DashboardSecondaryInsightsSkeleton />
          )
        ) : (
          <DashboardSecondaryInsightsSkeleton />
        )}
      </div>
    </div>
  );
};

// Financement des types de projet
const ProjectFinancement = ({ title, finance, total, financeTotal }) => {
  const [progress, setProgress] = useState(13);
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setProgress(
          Math.ceil(
            (finance / Math.ceil(financeTotal[0] + financeTotal[1])) * 100
          )
        ),
      500
    );
    return () => clearTimeout(timer);
  }, [finance]);
  return (
    <div>
      <div className="  mx-0  my-4 flex  w-full flex-row items-center justify-between">
        <p className="w-2/3"> {title} :</p>
        <div className=" relative w-1/3">
          <p
            className={`w-full absolute text-center z-[49]  font-semibold ${
              progress < 45 ? "text-verdeogreen" : "text-white"
            }`}
          >
            {Math.ceil(finance).toLocaleString("fr-FR")} €
          </p>
          <Progress value={progress} className="h-5 " />
        </div>
      </div>
    </div>
  );
};
// pourcentage de financement total
const SliderFinance = ({ but, money, loading }) => {
  const [percent, setPercent] = useState(0);
  const { t } = useTranslation("dashboard", {
    keyPrefix: "common",
  });

  useMemo(() => {
    setPercent(Math.ceil((money / but) * 100));
  }, [but, money]);

  return (
    <div className="relative flex flex-col" id="suivi-financement">
      <div className="grid grid-cols-2 justify-between justify-items-stretch">
        <p className="whitespace-nowrap text-card-foreground">
          {" "}
          {t("Financements souhaités / Coût total projets")}{" "}
        </p>
        {money && but ? (
          <p className="justify-self-end">
            {money.toLocaleString("fr-FR")} € / {but.toLocaleString("fr-FR")} €
          </p>
        ) : (
          <p className="justify-self-end"> {""}</p>
        )}
      </div>
      {loading ? (
        <Skeleton className="w-full h-3 rounded-[30px] bg-[#ecedee]" />
      ) : (
        <Slider
          className="w-full"
          disabled={true}
          defaultValue={[0]}
          value={[percent]}
          max={100}
          step={1}
        />
      )}
    </div>
  );
};

// Skeleton
const CarouselSkeleton = () => {
  return (
    <div>
      <div>
        <Carousel
          opts={{
            align: "start",
          }}
          className="m-5 w-auto"
        >
          <CarouselContent>
            {Array.from({ length: 8 }).map((_, i) => (
              <CarouselItem
                key={i}
                className="md:basis-1/5 lg:basis-1/6 xl:basis-[13%]"
              >
                <div className="p-1">
                  <Skeleton className="rounded-[15px] w-full h-[100px]" />
                  <Skeleton className="rounded-[15px] w-full h-[20px] my-2" />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </div>
      <div></div>
    </div>
  );
};

const TypeSkeleton = () => {
  return (
    <div className="m-5">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="  mx-0  my-4 flex  w-full flex-row items-center justify-between"
        >
          <Skeleton className="w-1/3 h-5 bg-[#ecedee] rounded-[20px]" />
          <Skeleton className="w-1/3 h-5 bg-[#ecedee] rounded-[20px]" />
        </div>
      ))}
    </div>
  );
};
